import { useTranslation } from 'react-i18next';
import { IconButton } from 'qampo-ui-library';
import { DatePicker } from 'qampo-ui-library/base';
import { IconChevronDown, WarningSign } from '../../UI';
import { formatLongDate } from '../../../utils/weekDateUtils';
import { formatDate } from '../../../utils/dateUtils';
import styles from './WeekOverviewHeader.module.css';

interface WeekOverviewHeaderProps {
  weekNumber: number;
  weekStartDate: string;
  weekEndDate: string;
  locationName: string;
  lastModifiedDate: string;
  isViewingCurrentWeek: boolean;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onGoToToday: () => void;
  onJumpToDate: (date: Date) => void;
  onOpenCalendar: () => void;
  isCalendarDisabled?: boolean;
  unscheduledCount: number;
  onOpenUnscheduled: () => void;
}

export const WeekOverviewHeader = ({
  weekNumber,
  weekStartDate,
  weekEndDate,
  locationName,
  lastModifiedDate,
  isViewingCurrentWeek,
  onPreviousWeek,
  onNextWeek,
  onGoToToday,
  onJumpToDate,
  onOpenCalendar,
  isCalendarDisabled = false,
  unscheduledCount,
  onOpenUnscheduled
}: WeekOverviewHeaderProps) => {
  const { t, i18n } = useTranslation();

  const startDate = new Date(weekStartDate);
  const endDate = new Date(weekEndDate);
  const rangeLabel = `${formatLongDate(startDate, i18n.language)} – ${formatLongDate(endDate, i18n.language)}`;

  return (
    <header className={styles.header}>
      <div className={styles.titleColumn}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{t('nav-week-overview')}</h1>
          <span
            className={styles.weekLabelWrapper}
            title={t('week-overview-jump-to-date')}
            aria-label={t('week-overview-jump-to-date')}
          >
            <span className={styles.weekLabel}>
              {t('week-overview-week-label', { weekNumber })}
            </span>
            <span className={styles.weekDatePicker}>
              <DatePicker
                value={startDate}
                onChange={onJumpToDate}
                language={i18n.language}
                selectionMode="week"
                todayLabel={t('week-overview-this-month')}
              />
            </span>
          </span>
        </div>
        <p className={styles.weekRange}>{rangeLabel}</p>
        <div className={styles.metaRow}>
          <p className={styles.locationName}>{locationName}</p>
          {lastModifiedDate && (
            <>
              <span className={styles.metaSeparator}>·</span>
              <p className={styles.lastModified}>
                {t('last-modified')} {formatDate(lastModifiedDate, i18n.language)}
              </p>
            </>
          )}
        </div>
      </div>
      <div className={styles.actions}>
        {unscheduledCount > 0 && (
          <button
            type="button"
            className={styles.unscheduledButton}
            onClick={onOpenUnscheduled}
          >
            <WarningSign variant="warning" />
            {t('week-overview-unscheduled-button', { count: unscheduledCount })}
          </button>
        )}
        <button
          type="button"
          className={styles.openCalendarButton}
          onClick={onOpenCalendar}
          disabled={isCalendarDisabled}
        >
          {t('week-overview-open-calendar')}
        </button>
        <div className={styles.weekNav}>
          <span aria-label={t('week-overview-previous-week')} title={t('week-overview-previous-week')}>
            <IconButton
              icon={<IconChevronDown className={styles.navIconLeft} />}
              onClick={onPreviousWeek}
              size="default"
              iconColor="var(--color-text-primary)"
              buttonBgColor="var(--color-surface-primary)"
              classes={styles.navButton}
            />
          </span>
          <button
            type="button"
            className={styles.todayButton}
            onClick={onGoToToday}
            disabled={isViewingCurrentWeek}
          >
            {t('week-overview-today')}
          </button>
          <span aria-label={t('week-overview-next-week')} title={t('week-overview-next-week')}>
            <IconButton
              icon={<IconChevronDown className={styles.navIconRight} />}
              onClick={onNextWeek}
              size="default"
              iconColor="var(--color-text-primary)"
              buttonBgColor="var(--color-surface-primary)"
              classes={styles.navButton}
            />
          </span>
        </div>
      </div>
    </header>
  );
};
